import { Box, IconButton, Tooltip } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import {
  DismissSmall,
  ExternalLinkSmall,
  MaximizeSmall,
  MinimiseSmall,
} from '@wix/wix-ui-icons-common';
import React, { FC, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { helpCenterSlice } from '../../../common/reducers/helpCenter';
import {
  selectCurrentArticle,
  selectFullPageSidebarExtended,
} from '../../../common/selectors/helpCenter';
import { appRoutes } from '../../../react-router';
import { TLocalesKeys } from '../../../types';
import { isSmartphone } from '../../../util';
import { biHelpCenter } from '../../../util/bi';
import { articleSidebarDataHooks } from './ArticleSidebar.dataHooks';

export const ArticleSidebarActions: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const article = useSelector(selectCurrentArticle);
  const sidebarExtended = useSelector(selectFullPageSidebarExtended);
  const extendedTooltipTextKey: TLocalesKeys = sidebarExtended
    ? 'sidebar-action-minimize-article'
    : 'sidebar-action-maximize-article';

  const closeArticle = useCallback(() => {
    if (!article) {
      return;
    }

    biHelpCenter.logCloseFullPageArticleSidebar(article.title, article.id);
    navigate(appRoutes.home());
  }, [navigate, article]);

  const toggleSidebarExtended = useCallback(() => {
    if (!article) {
      return;
    }
    biHelpCenter.logSectionInteraction(
      !sidebarExtended,
      article.title,
      article.id,
    );
    dispatch(helpCenterSlice.actions.setSidebarExtended(!sidebarExtended));
  }, [article, dispatch, sidebarExtended]);

  const articleExternalLinkClicked = useCallback(() => {
    if (!article) {
      return;
    }
    biHelpCenter.logArticleExternalLink(article.id, article.title, article.url);
  }, [article]);

  return (
    <Box gap={1}>
      {!isSmartphone && (
        <Tooltip
          content={t<TLocalesKeys>(extendedTooltipTextKey)}
          placement="bottom"
          size="small"
        >
          <IconButton
            priority="secondary"
            size="tiny"
            onClick={toggleSidebarExtended}
            dataHook={articleSidebarDataHooks.toggleModeButton()}
          >
            {sidebarExtended ? <MinimiseSmall /> : <MaximizeSmall />}
          </IconButton>
        </Tooltip>
      )}
      <Tooltip
        content={t<TLocalesKeys>('sidebar-action-new-tab')}
        placement="bottom"
        size="small"
      >
        <IconButton
          priority="secondary"
          size="tiny"
          as="a"
          href={article?.url}
          onClick={articleExternalLinkClicked}
          target="_blank"
          dataHook={articleSidebarDataHooks.externalLink()}
        >
          <ExternalLinkSmall />
        </IconButton>
      </Tooltip>
      <Tooltip
        content={t<TLocalesKeys>('sidebar-action-close')}
        placement="bottom"
        size="small"
      >
        <IconButton
          priority="secondary"
          size="tiny"
          onClick={closeArticle}
          dataHook={articleSidebarDataHooks.closeButton()}
        >
          <DismissSmall />
        </IconButton>
      </Tooltip>
    </Box>
  );
};
